import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { extras, light, fontStack } from "styles/tokens";
import "./Toast.css";

/**
 * App-wide, non-blocking toast notifications — replaces window.alert().
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success("Saved.");
 *   toast.error("Failed to download invoice");
 *
 * One <ToastProvider> is mounted near the app root. Toasts auto-dismiss
 * (errors linger longer) and can be closed manually. Errors announce
 * assertively to screen readers; everything else politely.
 */

const ToastContext = createContext(null);

let idCounter = 0;

const TOAST_VARS = {
  "--toast-font": fontStack,
  "--toast-surface": light.surface,
  "--toast-text": light.textHigh,
  "--toast-radius": `${extras.radius.md}px`,
  "--toast-shadow": extras.shadow.lg,
  "--toast-success": extras.feedback.success,
  "--toast-error": extras.feedback.danger,
  "--toast-info": extras.feedback.info,
  "--toast-warning": extras.feedback.warning,
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timersRef.current[id];
    if (timer) {
      clearTimeout(timer);
      delete timersRef.current[id];
    }
  }, []);

  const notify = useCallback(
    (opts) => {
      const o = typeof opts === "string" ? { message: opts } : opts || {};
      const id = ++idCounter;
      const type = o.type || "info";
      const message = o.message || "";
      // action: { label, onClick } — renders a button and makes the toast
      // sticky by default (duration 0), since an actionable toast the user
      // hasn't acted on shouldn't vanish on its own.
      const action =
        o.action && o.action.label && typeof o.action.onClick === "function"
          ? o.action
          : null;
      const duration =
        o.duration != null
          ? o.duration
          : action
            ? 0
            : type === "error"
              ? 6000
              : 4000;
      setToasts((list) => [...list, { id, type, message, action }]);
      if (duration > 0) {
        timersRef.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      notify,
      dismiss,
      success: (message, opts) => notify({ ...opts, type: "success", message }),
      error: (message, opts) => notify({ ...opts, type: "error", message }),
      info: (message, opts) => notify({ ...opts, type: "info", message }),
      warning: (message, opts) => notify({ ...opts, type: "warning", message }),
    }),
    [notify, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="toast-viewport"
        role="region"
        aria-label="Notifications"
        style={TOAST_VARS}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast-${t.type}`}
            role={t.type === "error" ? "alert" : "status"}
          >
            <span className="toast-msg">{t.message}</span>
            {t.action ? (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  t.action.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Returns { notify, success, error, info, warning, dismiss }.
 * Falls back to window.alert if no provider is mounted so a message is never
 * silently lost.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    const fn = (message) =>
      window.alert(typeof message === "string" ? message : "");
    return {
      notify: (o) => fn(typeof o === "string" ? o : o?.message),
      success: fn,
      error: fn,
      info: fn,
      warning: fn,
      dismiss: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
